import { useRef, useState, useEffect, useMemo } from 'react';
import { useIntl } from 'dibs-react-intl';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import {
    useFacetReducer,
    modifyFacets,
    SET_FACETS,
    CHANGE_PAGE,
    type FacetFilterAction,
    type FacetFilter,
    type UseFacetReducer,
} from './useFacetReducer.ts';
import {
    useHistoryReducer,
    isPhotoPath,
    isPhotosPath,
    NAVIGATE,
    CHANGE_PATH,
    type UseHistoryReducer,
} from './useHistoryReducer.ts';
import {
    usePhotoModalReducer,
    SET_MODAL_OPEN,
    SET_MODAL_PHOTO,
    type UsePhotoModalReducer,
} from './usePhotoModalReducer.ts';
import {
    getPhotoPageTitle,
    getTitleDescription,
    getUrlFacetFilterFromUrl,
    getUrlFromFacetFilter,
    getPathFacetNames,
} from '../../shared/helpers/photosFacetFilterUrlPathHelper.ts';
import { mapRelayFacets } from './mapRelayFacets.ts';
import { trackPaginationClick } from './photosSearchBrowseTracking.ts';
import { PAGE_SIZE } from '../constants.ts';

import { type RelayRefetchProp } from 'react-relay/legacy.js';
import {
    type RelayVariables,
    type PhotosEdges,
    type RelayFacets,
    type ModifyFacetPayload,
} from '../types.ts';

type FacetChangeHandler = (payload: {
    action: FacetFilterAction;
    payload?: Partial<ModifyFacetPayload>;
}) => void;

type usePhotosSearchBrowseStatePayload = {
    relay: RelayRefetchProp;
    originalUrl: string;
    facets?: RelayFacets;
    relayVariables: RelayVariables;
    photosEdges: PhotosEdges;
    numberOfPages: number;
    totalResults: number;
};

type PhotosSearchBrowseState = {
    originalUrl: string;
    pathFacets: string[];
    facetFilter: FacetFilter;
    facetFilterPath: string;
    modalPhoto: UsePhotoModalReducer['modalPhoto'];
    isLoading: boolean;
    // handlers, getters, dispatchers
    getPaginationHref: (pageNum: number) => string;
    handleFacetChange: FacetChangeHandler;
    handlePaginate: (newPage: number) => void;
    dispatchHistory: UseHistoryReducer['dispatchHistory'];
    dispatchFacetFilter: UseFacetReducer['dispatchFacetFilter'];
    dispatchModalPhoto: UsePhotoModalReducer['dispatchModalPhoto'];
};

export function usePhotosSearchBrowseState({
    originalUrl: originalUrlProp,
    facets,
    relayVariables,
    relay,
    numberOfPages,
    photosEdges,
    totalResults,
}: usePhotosSearchBrowseStatePayload): PhotosSearchBrowseState {
    const { formatMessage } = useIntl();
    const [originalUrl, setOriginalUrl] = useState(originalUrlProp);
    const [isLoading, setLoading] = useState(false);
    const isFirstRunRef = useRef(true);
    const { facetFilter, dispatchFacetFilter } = useFacetReducer({ facets, relayVariables });
    const [facetFilterPath, setFacetFilterPath] = useState('');
    const { history, dispatchHistory } = useHistoryReducer({
        path: originalUrlProp,
    });
    const { modalPhoto, dispatchModalPhoto } = usePhotoModalReducer();

    const [pathFacets, setPathFacets] = useState(getPathFacetNames(originalUrl, facets));

    const { first: itemsOnPage, page, searchTerm, style, room, project, creator } = facetFilter;

    const photoNodes = useMemo(
        () => (photosEdges || []).map(edge => edge?.node ?? null).filter(filterNulls),
        [photosEdges]
    );

    const photoNodesRef = useRef(photoNodes);
    useEffect(() => {
        photoNodesRef.current = photoNodes;
    }, [photoNodes]);

    const nextPhotoIndexRef = useRef(0);
    useEffect(() => {
        nextPhotoIndexRef.current = modalPhoto.nextPhotoIndex;
    }, [modalPhoto.nextPhotoIndex]);

    useEffect(() => {
        setFacetFilterPath(getUrlFromFacetFilter(facetFilter));
    }, [facetFilter]);

    useEffect(() => {
        if (isPhotosPath(history.path)) {
            setOriginalUrl(history.path);
        }
    }, [history.path]);

    useEffect(() => {
        if (isFirstRunRef.current) {
            return;
        }

        if (isPhotosPath(history.path)) {
            setPathFacets(getPathFacetNames(history.path, facets));
        }
    }, [modalPhoto.isOpen, history.path, facets]);

    useEffect(() => {
        let pageTitle = '';
        if (modalPhoto.isOpen) {
            const photo = modalPhoto.photo;
            const photoName = photo?.name || photo?.room?.name || '';
            const designFirm = photo?.designFirm?.name || '';
            pageTitle = getPhotoPageTitle({ photo: photoName, designFirm, formatMessage });
        } else {
            const { title } = getTitleDescription({
                facet: pathFacets.join(' '),
                count: totalResults,
                formatMessage,
            });
            pageTitle = title;
        }

        // Update pages title
        if (pageTitle && window && window.document.title !== pageTitle) {
            window.document.title = pageTitle;
        }
    }, [modalPhoto.isOpen, modalPhoto.photo, pathFacets, totalResults, formatMessage]);

    // Effect used to refetch photos, fires when filtered facets reducer changed
    useEffect(() => {
        if (isFirstRunRef.current) {
            return;
        }
        const renderVariables = {
            first: PAGE_SIZE,
            page,
            searchTerm,
            style: style.map(facet => facet.value),
            room: room.map(facet => facet.value),
            project: project.map(facet => facet.value),
            creator: creator.map(facet => facet.value),
        };
        setLoading(true);
        if (relay) {
            relay.refetch(renderVariables, null, () => {
                setLoading(false);
            });
        }
    }, [page, searchTerm, style, room, project, creator, relay]);

    // Effect used to navigate to modal photo when next/prev pages need to be fetched first
    useEffect(() => {
        const photoPath = photoNodes?.[nextPhotoIndexRef.current]?.url;
        if (
            modalPhoto.isOpen &&
            photoPath &&
            photoPath !== window.location.pathname &&
            !isLoading
        ) {
            dispatchHistory({
                type: NAVIGATE,
                path: photoPath,
            });
        }
    }, [photoNodes, isLoading, modalPhoto.isOpen, dispatchHistory]);

    // Effect used for facet filtering, fires on window location path changes
    useEffect(() => {
        if (isFirstRunRef.current) {
            return;
        }

        /**
         * We should only refetch when path was really changed
         * This will skip when Photo modal is being closed - path changed from /photo to /photos,
         * but according to filtered facets we should not do any refetch
         */
        const isPhotos = isPhotosPath(history.path);
        if (
            isPhotos &&
            history.path !==
                handleLocaleUrl(getUrlFromFacetFilter(facetFilter), GLOBAL_CLIENT_ONLY_LOCALE)
        ) {
            const variables = getUrlFacetFilterFromUrl(history.path);
            dispatchFacetFilter({
                type: SET_FACETS,
                facets: {
                    searchTerm: variables.searchTerm || '',
                    page: variables.page,
                    ...mapRelayFacets({ facets, variables }),
                },
            });
        } else if (isPhotos && modalPhoto.isOpen) {
            dispatchModalPhoto({ type: SET_MODAL_OPEN, isOpen: false });
        }
    }, [
        history.path,
        facetFilter,
        facets,
        modalPhoto.isOpen,
        dispatchFacetFilter,
        dispatchModalPhoto,
    ]);

    // Effect used to update single photo modal state
    useEffect(() => {
        if (isFirstRunRef.current) {
            return;
        }

        if (isPhotoPath(history.path)) {
            const photoIndex = photoNodes.findIndex(
                node => handleLocaleUrl(node.url, GLOBAL_CLIENT_ONLY_LOCALE) === history.path
            );
            const photoNode = photoNodes[photoIndex] || null;

            if (photoIndex > -1) {
                dispatchModalPhoto({
                    type: SET_MODAL_PHOTO,
                    photo: photoNode,
                    photoIndex,
                });
                dispatchModalPhoto({ type: SET_MODAL_OPEN, isOpen: true });
            }
        }
    }, [itemsOnPage, photoNodes, history.path, dispatchModalPhoto]);

    /**
     * Effect used to handle window history pop state:
     * If photo by URL was found then we dispatch CHANGE_PATH so it is handled
     * and photo shown in modal.
     * If photo by URL was not found then we reload the page to open
     * single photo page.
     */
    useEffect(() => {
        if (isFirstRunRef.current) {
            return;
        }

        const photoIndex = photoNodesRef.current.findIndex(node => node.url === history.poppedPath);

        if (isPhotosPath(history.poppedPath) || photoIndex > -1) {
            dispatchHistory({ type: CHANGE_PATH, path: history.poppedPath });
        } else {
            window.location.reload();
        }
    }, [history.poppedPath, dispatchHistory]);

    useEffect(() => {
        if (isFirstRunRef.current) {
            isFirstRunRef.current = false;
        }
    });

    const handleFacetChange: FacetChangeHandler = ({ action, payload }) => {
        dispatchHistory({
            type: NAVIGATE,
            path: getUrlFromFacetFilter(
                modifyFacets({
                    action,
                    facets,
                    facetFilter,
                    payload,
                })
            ),
        });
    };

    function getPaginationHref(pageNumber: number): string {
        return getUrlFromFacetFilter(
            modifyFacets({
                action: CHANGE_PAGE,
                facets,
                facetFilter,
                payload: { page: pageNumber },
            })
        );
    }

    function handlePaginate(newPage: number): void {
        trackPaginationClick({ ratio: `${newPage}/${numberOfPages}` });
        handleFacetChange({
            action: CHANGE_PAGE,
            payload: { page: newPage },
        });
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    }

    return {
        originalUrl,
        pathFacets,
        facetFilter,
        facetFilterPath,
        modalPhoto,
        isLoading,
        // handlers, getters, dispatchers
        getPaginationHref,
        handleFacetChange,
        handlePaginate,
        dispatchHistory,
        dispatchFacetFilter,
        dispatchModalPhoto,
    };
}
